import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { AppRoot } from '@bbkAdminComponents/app-layout/app-root';
import { SuspenseLoader } from '@bbkAdminComponents/loaders/suspense-loader';
import { LogInRouter } from '@bbkAdminComponents/log-in-flow/log-in.router';
import { useAppInit } from '@bbkAdminRedux/app/hooks';
import { selectCompany, selectUser } from '@bbkAdminRedux/app/selectors';
import { isUnauthorizedError } from '@bbkAdminRedux/rtkq/auth-status';
import { selectAuthStatus } from '@bbkAdminRedux/rtkq/auth-utils';
import {
  currentUserSlice,
  useIdentifyUser,
} from '@bbkAdminRedux/rtkq/current-user.slice';
import { useQueryFeatureFlags } from '@bbkAdminRedux/rtkq/feature-flags.slice';
import { useUpgradeSession } from '@bbkAdminRedux/rtkq/use-upgrade-session';
import { NoApps } from '@bbkAdminRouter/no-apps.page';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

export const AuthRoot: React.FC = () => {
  useUpgradeSession();
  useIdentifyUser();
  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const { isLoading: isFFLoading } = useQueryFeatureFlags();

  const [fetchUser$] = currentUserSlice.endpoints.getCurrentUser.useLazyQuery();
  const appInit = useAppInit();

  // Signed-in state is a single derived value from the /api/current_user probe
  // (the server's authority on the session), not a JS-visible JWT cookie and
  // not a component-local copy that can drift. Until it resolves we render the
  // loader instead of flashing the sign-in page.
  const authStatus = useSelector(selectAuthStatus);

  useEffect(() => {
    // Force a fresh probe on mount (no preferCacheValue): a retained fulfilled
    // entry must not stand in for a live session after the app remounts.
    fetchUser$(undefined, false)
      .unwrap()
      .then((u) => {
        appInit(u);
      })
      .catch((err: FetchBaseQueryError) => {
        if (isUnauthorizedError(err)) {
          appInit(undefined);
        } else {
          // Transient probe failure (network / 5xx): leave any previously
          // established session intact (the selector retains it) and just log.
          GlobalLogger.error(err);
        }
      });
  }, [appInit, fetchUser$]);

  // Sign-in no longer populates the global getCurrentUser query (post-login
  // validation is a non-caching request), so `authStatus` only becomes
  // authenticated from the boot probe, whose `.then` runs appInit before React
  // commits the fulfilled render. There is therefore no "authenticated but
  // uninitialized" window to gate here.
  if (isFFLoading || authStatus === 'loading') {
    return (
      <div className="tw-h-screen">
        <SuspenseLoader message=" " />
      </div>
    );
  }

  if (authStatus === 'unauthenticated') {
    return <LogInRouter />;
  }

  if (company && company.applications.length === 0) {
    return <NoApps company={company} />;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="bbk-authRoot tw-box-content">
      <AppRoot />
    </div>
  );
};
